import React, { useState, useEffect, useRef } from 'react';
import { Bell, User, LogOut, Calendar, ChevronDown, Check, Sun, Moon, Minimize2, AlignJustify, Camera, X, Trash2, Edit, Cake } from 'lucide-react';
import { NotificationCenter } from './NotificationCenter';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout, getCurrentUser } from '../../store/slices/authSlice';
import { toggleTheme, toggleDensity } from '../../store/slices/uiSlice';
import { useNavigate } from 'react-router-dom';
import {
  useGetAnneesAcademiquesQuery,
} from '../../services/anneeAcademiqueApi';
import { useUpdatePhotoMutation, useDeletePhotoMutation, useUpdateProfileMutation, useChangerMotDePasseMutation } from '../../services/coreApi';
import { setAnneeAcademique, getAnneeAcademique } from '../../services/baseApi';

export const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const theme = useAppSelector((state) => state.ui.theme);
  const density = useAppSelector((state) => state.ui.density);

  // Profil
  const [showProfile, setShowProfile] = useState(false);
  const [updatePhoto, { isLoading: uploadingPhoto }] = useUpdatePhotoMutation();
  const [deletePhoto] = useDeletePhotoMutation();
  const [updateProfile] = useUpdateProfileMutation();
  const [changerMdp, { isLoading: changingMdp }] = useChangerMotDePasseMutation();
  const [profileForm, setProfileForm] = useState({ nom: '', prenoms: '', email: '' });
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profileTab, setProfileTab] = useState<'infos' | 'mdp'>('infos');
  const [mdpForm, setMdpForm] = useState({ ancien: '', nouveau: '', confirmation: '' });
  const [mdpMsg, setMdpMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Suggestion photo (affichée 1 fois si pas de photo)
  const [showPhotoSuggestion, setShowPhotoSuggestion] = useState(false);
  useEffect(() => {
    if (user && !user.photo_url) {
      const dismissed = localStorage.getItem(`photo_suggestion_${user.id}`);
      if (!dismissed) setShowPhotoSuggestion(true);
    }
  }, [user]);
  const dismissPhotoSuggestion = () => {
    if (user) localStorage.setItem(`photo_suggestion_${user.id}`, '1');
    setShowPhotoSuggestion(false);
  };

  const handlePhotoUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { setProfileMsg({ type: 'error', text: 'La photo dépasse 5 Mo' }); return; }
    const fd = new FormData();
    fd.append('photo', file);
    try {
      await updatePhoto(fd).unwrap();
      dispatch(getCurrentUser());
      setProfileMsg({ type: 'success', text: 'Photo mise à jour' });
      dismissPhotoSuggestion();
    } catch (err: any) { setProfileMsg({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Supprimer votre photo de profil ?')) return;
    try {
      await deletePhoto().unwrap();
      dispatch(getCurrentUser());
      setProfileMsg({ type: 'success', text: 'Photo supprimée' });
    } catch { setProfileMsg({ type: 'error', text: 'Erreur' }); }
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile(profileForm).unwrap();
      dispatch(getCurrentUser());
      setProfileMsg({ type: 'success', text: 'Profil mis à jour' });
    } catch { setProfileMsg({ type: 'error', text: 'Erreur' }); }
  };

  const handleChangerMdp = async () => {
    setMdpMsg(null);
    if (mdpForm.nouveau.length < 8) {
      setMdpMsg({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères' });
      return;
    }
    if (mdpForm.nouveau !== mdpForm.confirmation) {
      setMdpMsg({ type: 'error', text: 'La confirmation ne correspond pas au nouveau mot de passe' });
      return;
    }
    if (mdpForm.ancien === mdpForm.nouveau) {
      setMdpMsg({ type: 'error', text: 'Le nouveau mot de passe doit être différent de l\'ancien' });
      return;
    }
    try {
      const res: any = await changerMdp({
        ancien_mot_de_passe: mdpForm.ancien,
        nouveau_mot_de_passe: mdpForm.nouveau,
      }).unwrap();
      setMdpMsg({ type: 'success', text: res.message || 'Mot de passe modifié' });
      setMdpForm({ ancien: '', nouveau: '', confirmation: '' });
    } catch (err: any) {
      setMdpMsg({ type: 'error', text: err?.data?.error || 'Erreur de modification' });
    }
  };

  // État pour le dropdown d'année
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [currentAnnee, setCurrentAnnee] = useState<string | null>(getAnneeAcademique());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Récupérer les années disponibles
  const { data: annees, isLoading: isLoadingAnnees } = useGetAnneesAcademiquesQuery();
  // Fix 2026-08-25 : `useActiverAnneeMutation` retiré du header — la bascule
  // est purement locale (localStorage). La mutation reste disponible dans
  // pages/Annee/AnneeAcademique.tsx pour le bouton « Activer » officiel.
  const isActivating = false;

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsYearDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mettre à jour l'année courante depuis le localStorage
  useEffect(() => {
    const storedAnnee = getAnneeAcademique();
    if (storedAnnee) {
      setCurrentAnnee(storedAnnee);
    } else if (annees && annees.length > 0) {
      // Trouver l'année active
      const anneeActive = annees.find(a => a.est_active);
      if (anneeActive) {
        setCurrentAnnee(anneeActive.libelle);
        setAnneeAcademique(anneeActive.libelle, anneeActive.id);
      }
    }
  }, [annees]);

  const handleSelectAnnee = (annee: { id: number; libelle: string; est_active: boolean; est_cloturee: boolean }) => {
    if (annee.est_cloturee) return;

    // Fix 2026-08-25 : BASCULE LOCALE uniquement (ne PAS appeler activerAnnee).
    // L'ancienne version appelait POST /annees-academiques/{id}/activer/ qui
    // écrivait `est_active=True` en base — impactant TOUS les utilisateurs
    // (formateurs, étudiants, parents, portails) qui basculaient sur la même
    // année sans le vouloir. Le choix d'année dans le dropdown du header doit
    // rester une préférence de consultation LOCALE : localStorage → header
    // X-Annee-Academique → SchemaMiddleware. Pour changer l'année officielle
    // en base (celle utilisée par les portails), passer par la page
    // « Gestion des années académiques » avec son bouton « Activer » explicite.
    setAnneeAcademique(annee.libelle, annee.id);
    setCurrentAnnee(annee.libelle);
    setIsYearDropdownOpen(false);

    // Recharger la page pour que toutes les requêtes RTK Query réémettent
    // avec le nouveau header X-Annee-Academique.
    window.location.reload();
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4">
        <div className="hidden sm:flex items-center space-x-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">Système de Gestion des Notes</span>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4 ml-auto">
          {/* Sélecteur d'Année Académique */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
              disabled={isLoadingAnnees || isActivating}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition disabled:opacity-50"
            >
              <Calendar size={18} className="text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                {isLoadingAnnees ? 'Chargement...' : currentAnnee || 'Sélectionner'}
              </span>
              <ChevronDown
                size={16}
                className={`text-blue-600 dark:text-blue-400 transition-transform ${isYearDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown */}
            {isYearDropdownOpen && annees && annees.length > 0 && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Année Académique</p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {annees.map((annee) => (
                    <button
                      key={annee.id}
                      onClick={() => handleSelectAnnee(annee)}
                      disabled={annee.est_cloturee || isActivating}
                      className={`w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                        annee.est_cloturee ? 'opacity-50 cursor-not-allowed' : ''
                      } ${currentAnnee === annee.libelle ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{annee.libelle}</span>
                        {annee.est_active && (
                          <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded">
                            Active
                          </span>
                        )}
                        {annee.est_cloturee && (
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">
                            Clôturée
                          </span>
                        )}
                      </div>
                      {currentAnnee === annee.libelle && (
                        <Check size={16} className="text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setIsYearDropdownOpen(false);
                      navigate('/annee-academique');
                    }}
                    className="w-full text-center text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 py-1"
                  >
                    Gérer les années
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Anniversaires du jour */}
          <AnniversairesButton />

          {/* Notifications (messagerie chat) */}
          <NotificationCenter />

          {/* Theme Toggle */}
          <button
            onClick={() => dispatch(toggleTheme())}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Density Toggle */}
          <button
            onClick={() => dispatch(toggleDensity())}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            title={density === 'compact' ? 'Mode confortable' : 'Mode compact'}
          >
            {density === 'compact' ? <AlignJustify size={20} /> : <Minimize2 size={20} />}
          </button>

          {/* User Menu */}
          <div className="flex items-center space-x-2 sm:space-x-3 border-l dark:border-gray-700 pl-2 sm:pl-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {user?.nom} {user?.prenoms}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{user?.role}</p>
            </div>
            <div className="relative">
              <button
                onClick={() => { setShowProfile(true); setProfileForm({ nom: user?.nom || '', prenoms: user?.prenoms || '', email: user?.email || '' }); setProfileMsg(null); }}
                className="flex items-center space-x-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition"
                title="Mon profil"
              >
                {user?.photo_url ? (
                  <img src={user.photo_url} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-blue-400" />
                ) : (
                  <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white">
                    <User size={16} />
                  </div>
                )}
              </button>
              {/* Suggestion photo */}
              {showPhotoSuggestion && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-blue-200 dark:border-blue-700 p-3 z-50">
                  <div className="flex items-start gap-2">
                    <Camera size={18} className="text-blue-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100">Ajoutez votre photo de profil</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Personnalisez votre compte avec une photo</p>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => { setShowProfile(true); setProfileForm({ nom: user?.nom || '', prenoms: user?.prenoms || '', email: user?.email || '' }); dismissPhotoSuggestion(); }}
                          className="px-2 py-1 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Ajouter
                        </button>
                        <button onClick={dismissPhotoSuggestion} className="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700">
                          Plus tard
                        </button>
                      </div>
                    </div>
                    <button onClick={dismissPhotoSuggestion} className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
              title="Déconnexion"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Modal Profil */}
      {showProfile && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center relative">
              <button onClick={() => setShowProfile(false)} className="absolute top-3 right-3 p-1 text-white/70 hover:text-white hover:bg-white/10 rounded">
                <X size={18} />
              </button>
              {/* Photo */}
              <div className="relative inline-block">
                {user?.photo_url ? (
                  <img src={user.photo_url} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg mx-auto" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto border-4 border-white/30">
                    <User size={40} className="text-white/70" />
                  </div>
                )}
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute bottom-0 right-0 p-1.5 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                  title="Changer la photo"
                >
                  {uploadingPhoto ? <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /> : <Camera size={14} className="text-blue-600" />}
                </button>
                <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); if (photoInputRef.current) photoInputRef.current.value = ''; }} />
              </div>
              {user?.photo_url && (
                <button onClick={handleDeletePhoto}
                  className="mt-2 text-white/60 hover:text-white text-[10px] flex items-center gap-1 mx-auto">
                  <Trash2 size={10} /> Supprimer la photo
                </button>
              )}
              <h3 className="text-white font-bold text-lg mt-3">{user?.nom} {user?.prenoms}</h3>
              <p className="text-blue-200 text-sm">{
                user?.role === 'super_admin' ? 'Super Administrateur'
                : user?.role === 'admin' ? 'Administrateur'
                : user?.role === 'agent' ? 'Agent Scolarité'
                : user?.role === 'directeur' ? 'Directeur (lecture seule)'
                : 'Consultation'
              }</p>
              <p className="text-blue-300 text-xs mt-1">@{user?.username}</p>
            </div>

            {/* Onglets */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setProfileTab('infos')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                  profileTab === 'infos'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >👤 Mes infos</button>
              <button
                onClick={() => setProfileTab('mdp')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                  profileTab === 'mdp'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >🔑 Mot de passe</button>
            </div>

            {profileTab === 'infos' && (
              <>
                <div className="p-6 space-y-4">
                  {profileMsg && (
                    <div className={`p-2 rounded text-xs ${profileMsg.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'}`}>
                      {profileMsg.text}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom</label>
                      <input type="text" value={profileForm.nom} onChange={e => setProfileForm(f => ({ ...f, nom: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Prénoms</label>
                      <input type="text" value={profileForm.prenoms} onChange={e => setProfileForm(f => ({ ...f, prenoms: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Email</label>
                    <input type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                  <button onClick={() => setShowProfile(false)} className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300">Fermer</button>
                  <button onClick={handleSaveProfile} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Enregistrer</button>
                </div>
              </>
            )}

            {profileTab === 'mdp' && (
              <>
                <div className="p-6 space-y-3">
                  {mdpMsg && (
                    <div className={`p-2 rounded text-xs ${mdpMsg.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'}`}>
                      {mdpMsg.text}
                    </div>
                  )}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-2 text-xs text-blue-800 dark:text-blue-200">
                    Choisissez un mot de passe d'au moins <strong>8 caractères</strong>.
                    Vous serez peut-être déconnecté(e) après modification.
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mot de passe actuel</label>
                    <input
                      type="password"
                      value={mdpForm.ancien}
                      onChange={e => setMdpForm(f => ({ ...f, ancien: e.target.value }))}
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={mdpForm.nouveau}
                      onChange={e => setMdpForm(f => ({ ...f, nouveau: e.target.value }))}
                      autoComplete="new-password"
                      minLength={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Confirmer le nouveau mot de passe</label>
                    <input
                      type="password"
                      value={mdpForm.confirmation}
                      onChange={e => setMdpForm(f => ({ ...f, confirmation: e.target.value }))}
                      autoComplete="new-password"
                      minLength={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    {mdpForm.confirmation && mdpForm.confirmation !== mdpForm.nouveau && (
                      <div className="text-xs text-red-600 mt-1">Les mots de passe ne correspondent pas</div>
                    )}
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                  <button onClick={() => setShowProfile(false)} className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300">Fermer</button>
                  <button
                    onClick={handleChangerMdp}
                    disabled={changingMdp || !mdpForm.ancien || !mdpForm.nouveau || !mdpForm.confirmation || mdpForm.nouveau !== mdpForm.confirmation}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {changingMdp ? '...' : '🔑 Changer le mot de passe'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};


// ─── Bouton + popover anniversaires du jour ───────────────────────

interface AnniversaireEtu {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
  age: number;
  photo_url: string | null;
  sexe: string;
}


type AnnivTab = 'aujourd' | 'prochains' | 'historique';

const AnniversairesButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<AnnivTab>('aujourd');
  const [data, setData] = useState<{ total: number; etudiants: AnniversaireEtu[] } | null>(null);
  const [prochains, setProchains] = useState<{ par_date: Record<string, any[]>; nb_total: number } | null>(null);
  const [historique, setHistorique] = useState<{ results: any[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const baseUrl = (process.env.REACT_APP_API_URL as string) || '/api';
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
    const annee = getAnneeAcademique();
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(annee ? { 'X-Annee-Academique': annee } : {}),
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/portail/admin/anniversaires-du-jour/`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };

  const loadProchains = async () => {
    setLoadingTab(true);
    try {
      const res = await fetch(`${baseUrl}/portail/admin/anniversaires-prochains/?jours=5`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setProchains(await res.json());
    } finally { setLoadingTab(false); }
  };

  const loadHistorique = async () => {
    setLoadingTab(true);
    try {
      const res = await fetch(`${baseUrl}/portail/admin/anniversaires-historique/?limit=30&jours=30`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setHistorique(await res.json());
    } finally { setLoadingTab(false); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger lazy quand on change d'onglet
  useEffect(() => {
    if (!open) return;
    if (tab === 'prochains' && !prochains) loadProchains();
    if (tab === 'historique' && !historique) loadHistorique();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const total = data?.total || 0;

  const tabBtn = (key: AnnivTab, label: string, badge?: number) => (
    <button
      onClick={() => setTab(key)}
      className={`flex-1 px-2 py-2 text-[11px] font-medium border-b-2 transition ${
        tab === key
          ? 'border-pink-500 text-pink-600 dark:text-pink-300'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
      }`}
    >
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold bg-pink-500 text-white rounded-full">
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-600 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-300 hover:bg-pink-50 dark:hover:bg-pink-900/20 rounded-lg transition"
        title={total > 0 ? `${total} anniversaire${total > 1 ? 's' : ''} aujourd'hui` : 'Aucun anniversaire aujourd\'hui'}
      >
        <Cake size={20} className={total > 0 ? 'text-pink-500' : ''} />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-pink-500 text-white rounded-full px-1">
            {total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[75vh] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <div className="flex items-center gap-2">
              <Cake size={16} className="text-pink-500" />
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Anniversaires</h3>
            </div>
            <div className="flex mt-2 -mb-3 border-b border-gray-200 dark:border-gray-700">
              {tabBtn('aujourd', "Aujourd'hui", total)}
              {tabBtn('prochains', '5 jours', prochains?.nb_total)}
              {tabBtn('historique', 'Historique')}
            </div>
          </div>

          {tab === 'aujourd' && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading && <div className="px-4 py-4 text-xs text-gray-500 text-center">Chargement…</div>}
              {!loading && total === 0 && (
                <div className="px-4 py-6 text-xs text-gray-500 text-center">Aucun anniversaire aujourd'hui.</div>
              )}
              {!loading && data?.etudiants.map((e) => (
                <EtudiantAnnivRow key={e.matricule} etu={e} />
              ))}
            </div>
          )}

          {tab === 'prochains' && (
            <div className="p-2">
              {loadingTab && <div className="px-2 py-4 text-xs text-gray-500 text-center">Chargement…</div>}
              {!loadingTab && (!prochains || prochains.nb_total === 0) && (
                <div className="px-2 py-6 text-xs text-gray-500 text-center">Aucun anniversaire dans les 5 prochains jours.</div>
              )}
              {!loadingTab && prochains && Object.entries(prochains.par_date).map(([dateIso, arr]) => {
                if (arr.length === 0) return null;
                const d = new Date(dateIso + 'T00:00:00');
                const libelle = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
                return (
                  <div key={dateIso} className="mb-3">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 px-2 mb-1 capitalize">{libelle}</div>
                    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
                      {arr.map((e) => (
                        <div key={e.matricule} className="flex items-center gap-2.5 px-2 py-2">
                          {e.photo_url ? (
                            <img src={e.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 flex items-center justify-center text-[10px] font-bold">
                              {(e.prenoms?.[0] || '') + (e.nom?.[0] || '')}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{e.prenoms} {e.nom}</div>
                            <div className="text-[10px] text-gray-500">{e.classe || '—'} · {e.age_a_venir ? `${e.age_a_venir} ans` : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'historique' && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {loadingTab && <div className="px-4 py-4 text-xs text-gray-500 text-center">Chargement…</div>}
              {!loadingTab && (!historique || historique.results.length === 0) && (
                <div className="px-4 py-6 text-xs text-gray-500 text-center">Pas d'anniversaire récent.</div>
              )}
              {!loadingTab && historique?.results.map((h: any) => {
                const d = new Date(h.date_anniversaire + 'T00:00:00');
                return (
                  <div key={`${h.date_anniversaire}-${h.matricule}`} className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {h.photo_url ? (
                        <img src={h.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 flex items-center justify-center text-xs font-bold">
                          {(h.prenoms?.[0] || '') + (h.nom?.[0] || '')}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {h.prenoms} {h.nom}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · {h.classe || '—'}
                        </div>
                        <div className="text-[10px] text-pink-600 dark:text-pink-400 mt-0.5">
                          {h.nb_messages} souhait{h.nb_messages > 1 ? 's' : ''} · {h.nb_likes_total} ❤
                          {h.nb_admin > 0 && <> · {h.nb_admin} scolarité</>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Ligne d'un étudiant qui fête + bouton "Souhaiter" inline
const EtudiantAnnivRow: React.FC<{ etu: any }> = ({ etu }) => {
  const [showInput, setShowInput] = useState(false);
  const [msg, setMsg] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const envoyer = async () => {
    if (!msg.trim()) return;
    setErr(null);
    setEnvoi(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL as string) || '/api';
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
      const res = await fetch(`${baseUrl}/portail/admin/anniversaires/${encodeURIComponent(etu.matricule)}/souhaiter/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ contenu: msg.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || `Erreur ${res.status}`);
      }
      setEnvoye(true);
      setMsg('');
      setTimeout(() => { setShowInput(false); setEnvoye(false); }, 2000);
    } catch (e: any) {
      setErr(e?.message || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center gap-3">
        {etu.photo_url ? (
          <img src={etu.photo_url} alt={etu.nom} className="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-gray-700" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 flex items-center justify-center text-xs font-bold">
            {(etu.prenoms?.[0] || '') + (etu.nom?.[0] || '')}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {etu.prenoms} {etu.nom}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {etu.matricule} · {etu.classe || '—'} · <strong className="text-pink-600 dark:text-pink-400">{etu.age} ans</strong>
          </div>
        </div>
        {!envoye && (
          <button
            onClick={() => setShowInput(!showInput)}
            className="text-[10px] px-2 py-1 bg-pink-600 hover:bg-pink-700 text-white rounded font-medium whitespace-nowrap"
            title="Souhaiter joyeux anniversaire au nom de la scolarité"
          >
            🎂 Souhaiter
          </button>
        )}
        {envoye && (
          <span className="text-[10px] text-green-600 dark:text-green-400 font-semibold whitespace-nowrap">✓ Envoyé</span>
        )}
      </div>
      {showInput && !envoye && (
        <div className="mt-2 flex gap-1.5">
          <input
            type="text"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder={`Message pour ${etu.prenoms}…`}
            maxLength={500}
            className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            onKeyDown={(e) => { if (e.key === 'Enter') envoyer(); }}
          />
          <button
            onClick={envoyer}
            disabled={envoi || !msg.trim()}
            className="px-2 py-1 text-xs bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white rounded"
          >
            {envoi ? '…' : 'OK'}
          </button>
        </div>
      )}
      {err && <div className="mt-1 text-[10px] text-red-500">{err}</div>}
    </div>
  );
};
